'use client';

import isPropValid from '@emotion/is-prop-valid';
import {
  Box,
  type BoxProps,
  ListItemButton,
  ListItemText,
  type ListItemTextProps,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';

interface StyledListItemTextProps extends ListItemTextProps {
  open?: boolean;
}

interface StyledMenuButtonBoxProps extends BoxProps {
  open?: boolean;
}

interface StyledBoxContentProps extends BoxProps {
  open?: boolean;
}

interface StyledSubmenuTextProps extends ListItemTextProps {
  open?: boolean;
  isActive?: boolean;
}

export const StyledMainDrawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    overflowX: 'hidden',
    scrollX: 'hidden',
    boxSizing: 'border-box',
    position: 'relative',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    width: theme.uiSizes.drawerWidth,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    height: '100vh',
    transition: theme.transitions.create(['width', 'padding', 'left'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.short,
    }),
    ...(!open && {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      width: theme.uiSizes.drawerWidthClosed,
    }),
    [theme.breakpoints.down('md')]: {
      position: 'absolute',
      top: theme.uiSizes.headerHeight,
      left: 0,
      border: 'none',
      height: `calc(100vh - ${theme.uiSizes.headerHeight}px)`,
      width: '100%',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      zIndex: theme.zIndex.drawer,
      ...(!open && {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        width: '100%',
        left: '-100%',
      }),
    },
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
}));

export const StyledListItemButton = styled(ListItemButton)(({ theme }) => {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    alignContent: 'center',
    alignItems: 'center',
    width: '100%',
    color: theme.palette.text.secondary,
    transition: theme.transitions.create(['background-color', 'color'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.short,
    }),
    '&.active': {
      pointerEvents: 'none',
    },
    '&.active, &:hover': {
      color: theme.palette.primary.light,
    },
  };
});

export const StyledLastLevelButton = styled(ListItemButton)(({ theme }) => {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    alignContent: 'center',
    alignItems: 'center',
    width: '100%',
    color: theme.palette.text.secondary,
    transition: theme.transitions.create(['background-color', 'color'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.short,
    }),
    '&.active': {
      pointerEvents: 'none',
    },
    '&.active, &:hover': {
      // backgroundColor: theme.palette.action.hover,
      color: theme.palette.grey[800],
    },
  };
});

export const StyledListItemMenuButton = styled(ListItemButton)(({ theme }) => {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    alignContent: 'center',
    alignItems: 'center',
    width: '100%',
    color: theme.palette.text.secondary,
    transition: theme.transitions.create(['background-color', 'color'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.short,
    }),
    '&.active': {
      pointerEvents: 'none',
    },
    '&.active, &:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.light,
    },
  };
});

export const getOpacity = (open?: boolean) => {
  if (open === undefined) return '1';
  return open ? '1' : '0';
};

export const StyledListItemText = styled(ListItemText, {
  shouldForwardProp: (prop: string) => isPropValid(prop),
})<StyledListItemTextProps>(({ theme, open }) => ({
  padding: 0,
  display: 'inline-block',
  flexGrow: 0,
  span: {
    fontSize: theme.typography.pxToRem(14),
    display: 'inline-block',
    opacity: getOpacity(open),
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.short,
    }),
  },
}));

export const StyledSubmenuText = styled(ListItemText, {
  shouldForwardProp: (prop: string) => isPropValid(prop) && prop !== 'isActive',
})<StyledSubmenuTextProps>(({ theme, open, isActive }) => ({
  padding: 0,
  display: 'inline-block',
  flexGrow: 0,
  span: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: isActive ? 600 : 400,
    color: isActive ? theme.palette.grey[800] : 'inherit',
    display: 'inline-block',
    opacity: getOpacity(open),
  },
}));

export const ActiveSubmenuIndicator = styled(Box)(({ theme }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: theme.palette.grey[800],
  marginRight: theme.spacing(1),
  flexShrink: 0,
}));

export const InactiveSubmenuIndicator = styled(Box)(({ theme }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: theme.palette.grey[400],
  marginRight: theme.spacing(1),
  flexShrink: 0,
}));

export const StyledMenuButtonBox = styled(Box, {
  shouldForwardProp: (prop: string) => isPropValid(prop),
})<StyledMenuButtonBoxProps>(({ theme, open }) => {
  return {
    padding: 0,
    width: '100%',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    position: 'relative',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.short,
    }),
    ...(open ? { right: 0 } : { right: '10px' }),
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  };
});

export const StyledBoxContent = styled(Box)<StyledBoxContentProps>(({
  theme,
  open,
}) => {
  return {
    flex: '1 1 auto',
    width: '100%',
    position: 'relative',
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    [theme.breakpoints.up('sm')]: {
      maxHeight: 'calc(100vh - 120px)',
    },
    [theme.breakpoints.down('sm')]: {
      maxHeight: `calc(100vh - ${theme.uiSizes.headerHeight}px - 100px)`,
      paddingBottom: theme.spacing(1),
    },
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.grey[300]} transparent`,
    '::-webkit-scrollbar': {
      width: '4px',
    },
    '::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[300],
      borderRadius: '2px',
    },
    '::-webkit-scrollbar-thumb:hover': {
      backgroundColor: theme.palette.grey[500],
    },
    '.logo-nav': {
      display: 'block',
      position: 'relative',
      margin: '0 auto',
      transition: theme.transitions.create(
        ['width', 'height', 'margin-top', 'margin-bottom'],
        {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.short,
        }
      ),
      ...(open
        ? {
            width: '100px',
            height: '100px',
            marginTop: `-${theme.spacing(2)}`,
            marginBottom: theme.spacing(1),
          }
        : {
            width: '50px',
            height: '50px',
            marginTop: `${theme.spacing(3)}`,
            marginBottom: `calc(${theme.spacing(2)} + 2px)`,
          }),
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
  };
});

export const StyledSignOutContainer = styled(Box)(({ theme }) => ({
  flex: '0 0 auto',
  width: '100%',
  marginTop: 'auto',
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
  minHeight: '60px',
}));
